import qs from 'qs'

export type InternalApiAction = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  endpoint: string
  body?: string | FormData
  headers?: (headers: Record<string, string>) => Record<string, string>
  /** Skip the auth-session step (no `ensureAccessToken`, no `Authorization`). For endpoints that must not drag in the JWT refresh routine, e.g. cookie writes. */
  skipAuth?: boolean
}

export type InternalApiActionDescriptor = {
  label: string
}

export type InternalApiActionCallback<RR> = {
  (dispatch: <R>(action: InternalApiAction) => Promise<R>): Promise<RR>
  [INTERNAL_API_ACTION_SYMBOL]: InternalApiActionDescriptor
}

export type InternalApiDispatcherDispatchOptions = {
  abortSignal?: AbortSignal
}

export interface InternalApiDispatcherInterface {
  dispatch: <RR>(
    apiAction: (dispatch: <R>(action: InternalApiAction) => Promise<R>, options?: InternalApiDispatcherDispatchOptions) => Promise<RR>,
    options?: InternalApiDispatcherDispatchOptions
  ) => Promise<RR>
}

export const INTERNAL_API_ACTION_SYMBOL = Symbol('INTERNAL_API_ACTION_SYMBOL')

export const createInternalApiAction = <RR>(
  label: string,
  cb: (dispatch: <R>(action: InternalApiAction) => Promise<R>) => Promise<RR>
): InternalApiActionCallback<RR> => {
  // front assigns onto `cb` directly; the app's stricter `noImplicitAny` will not index a function type.
  const action = cb as InternalApiActionCallback<RR>
  action[INTERNAL_API_ACTION_SYMBOL] = { label }
  return action
}

export const getInternalApiActionDescriptor = (apiAction: InternalApiActionCallback<any>): InternalApiActionDescriptor => apiAction[INTERNAL_API_ACTION_SYMBOL]

export const endpoint = (pathname: string, query?: Record<string, unknown>): string => {
  const search = query ? qs.stringify(query, { arrayFormat: 'brackets' }) : ''
  return search ? `${pathname}?${search}` : pathname
}

export const encode = (strings: TemplateStringsArray, ...values: unknown[]): string =>
  strings.reduce((acc, str, i) => acc + (i > 0 ? encodeURIComponent(String(values[i - 1])) : '') + str, '')
